import { Button } from "@/components/ui/button";
import { Phone, Video, ClipboardList } from "lucide-react";
import type { LandingCtaConfig, LandingButton } from "@/lib/landingConfig";

interface Props {
  cta: LandingCtaConfig;
  /** Default WhatsApp URL used when the CTA does not override the message. */
  whatsappUrl: string;
  /** Raw WhatsApp number (digits only, no plus sign) — used to build custom messages and tel: links. */
  whatsappNumber?: string;
  /** Anchor href for the on-page lead form (typically "#agendar"). */
  agendarHref?: string;
}

function resolveHref(btn: LandingButton | undefined, waUrl: string): string {
  if (!btn) return waUrl;
  if (btn.action === "url") return btn.url || "#";
  return waUrl;
}

const LandingCTA = ({ cta, whatsappUrl, whatsappNumber, agendarHref = "#agendar" }: Props) => {
  // If the CTA supplies its own WhatsApp message, rebuild the URL; otherwise fall back to the page's default.
  const waUrl =
    cta.whatsapp_message && whatsappNumber
      ? `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(cta.whatsapp_message)}`
      : whatsappUrl;

  const bg = cta.background_color || undefined;
  const btn = cta.button;
  const hasExtras = cta.show_phone || cta.show_call_button || cta.show_tour_button || cta.show_form;

  // Format the phone number for display: 55 21 9 7354-8387 → +55 21 97354-8387
  const phoneDisplay = whatsappNumber
    ? whatsappNumber.length === 13
      ? `+${whatsappNumber.slice(0, 2)} (${whatsappNumber.slice(2, 4)}) ${whatsappNumber.slice(4, 9)}-${whatsappNumber.slice(9)}`
      : whatsappNumber
    : "";

  return (
    <section
      id="cta"
      className="py-16 md:py-24"
      style={bg ? { backgroundColor: bg } : undefined}
    >
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center">
          {cta.image && (
            <div className="order-2 md:order-1">
              <img
                src={cta.image}
                alt={cta.title || "CTA"}
                className="w-full h-auto rounded-lg shadow-2xl"
                loading="lazy"
              />
            </div>
          )}
          <div className={cta.image ? "order-1 md:order-2" : "max-w-3xl mx-auto text-center"}>
            {cta.subtitle && (
              <p className="font-body text-sm uppercase tracking-widest text-accent mb-3">
                {cta.subtitle}
              </p>
            )}
            {cta.title && (
              <h2 className="font-display text-3xl md:text-5xl font-bold text-white leading-tight mb-4 drop-shadow">
                {cta.title}
              </h2>
            )}
            {cta.support_text && (
              <p className="font-body text-white/90 text-base md:text-lg mb-8 drop-shadow">
                {cta.support_text}
              </p>
            )}

            {cta.show_phone && phoneDisplay && (
              <p className="font-display text-2xl md:text-3xl font-bold text-accent mb-6 drop-shadow">
                {phoneDisplay}
              </p>
            )}

            {/* Primary CTA button */}
            {btn?.label && (
              <a
                href={resolveHref(btn, waUrl)}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button variant="gold" size="lg" className="text-lg px-10 py-6">
                  {btn.label}
                </Button>
              </a>
            )}

            {/* Extras row */}
            {hasExtras && (
              <div className="flex flex-wrap gap-3 mt-6 justify-start">
                {cta.show_call_button && whatsappNumber && (
                  <a href={`tel:+${whatsappNumber}`}>
                    <Button variant="heroOutline" size="lg">
                      <Phone className="w-5 h-5" /> Ligar agora
                    </Button>
                  </a>
                )}
                {cta.show_tour_button && cta.tour_url && (
                  <a href={cta.tour_url} target="_blank" rel="noopener noreferrer">
                    <Button variant="heroOutline" size="lg">
                      <Video className="w-5 h-5" /> Tour Virtual
                    </Button>
                  </a>
                )}
                {cta.show_form && (
                  <a href={agendarHref}>
                    <Button variant="heroOutline" size="lg">
                      <ClipboardList className="w-5 h-5" /> Preencher formulário
                    </Button>
                  </a>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default LandingCTA;
